// ============================================================
// PLUMME — AI Advisor: financial report sections
// ============================================================
// Summary, key figures, budget variance, liquidity, running balance, balance
// sheet and monthly result. Pure derivation: every figure comes from a model
// that is already derived elsewhere, because two screens must never derive the
// same number twice or they will eventually disagree.
//
// See aiAdvisorReportBlocks.jsx for the block contract these return.
const PlummeAiAdvisorReportFinance = (() => {
  const B = () => window.PlummeAiAdvisorReportBlocks;

  // ---------- Key figures ----------
  // Income, costs and result come from the report result for the year; the
  // comparison comes from the same derivation for the year before. With no
  // previous year the delta is dropped entirely rather than shown as 0 %.
  const keyFigures = ({ report, previousReport, previousYearAvailable, period }) => {
    const { num, round, round1, percentChange, deltaTone } = B();
    const income = num(report && report.inntekter);
    const costs = num(report && report.kostnader);
    const result = num(report && report.resultat);
    const margin = income > 0 ? (result / income) * 100 : null;
    const previousIncome = num(previousReport && previousReport.inntekter);
    const previousMargin = previousIncome > 0 ? (num(previousReport.resultat) / previousIncome) * 100 : null;
    const compare = { key: period?.quarter ? 'adv.rpt.period.vsQuarter' : 'adv.rpt.cell.vsPreviousYear' };
    const withDelta = (value, previous, betterWhen) => {
      if (!previousYearAvailable) return {};
      const delta = percentChange(value, previous);
      return delta == null ? {} : { delta, deltaTone: deltaTone(delta, betterWhen), compare };
    };
    return {
      type: 'kpis',
      items: [
        { id: 'income', label: { key: 'adv.rpt.kpi.income' }, value: round(income), format: 'currency', ...withDelta(income, previousIncome, 'high') },
        { id: 'costs', label: { key: 'adv.rpt.kpi.costs' }, value: round(costs), format: 'currency', ...withDelta(costs, num(previousReport && previousReport.kostnader), 'low') },
        { id: 'result', label: { key: 'adv.rpt.kpi.result' }, value: round(result), format: 'currency', ...withDelta(result, num(previousReport && previousReport.resultat), 'high') },
        {
          id: 'margin',
          label: { key: 'adv.rpt.kpi.margin' },
          value: margin == null ? null : round1(margin),
          format: 'percent',
          ...(previousMargin == null || margin == null || !previousYearAvailable
            ? {}
            : { delta: round1(margin - previousMargin), deltaTone: deltaTone(margin - previousMargin, 'high'), compare }),
        },
      ],
    };
  };

  const keyFiguresSection = (ctx) => {
    const { num, safeArray, round, section, note, chart, MONTH_KEYS } = B();
    const current = ctx.report || {};
    const previous = ctx.previousReport || {};
    const bookedMonths = safeArray(ctx.chart).slice(0, Math.max(0, num(ctx.monthsElapsed)));
    let cumulative = 0;
    const visual = ctx.previousYearAvailable
      ? chart('bars', {
        compact: true,
        titleKey: 'adv.rpt.chart.keyFiguresComparison',
        categories: [{ key: 'adv.rpt.kpi.income' }, { key: 'adv.rpt.kpi.costs' }, { key: 'adv.rpt.kpi.result' }],
        series: [
          { id: 'previous', labelKey: 'adv.rpt.chart.previousPeriod', values: [num(previous.inntekter), num(previous.kostnader), num(previous.resultat)] },
          { id: 'current', labelKey: 'adv.rpt.chart.currentPeriod', values: [num(current.inntekter), num(current.kostnader), num(current.resultat)] },
        ],
      })
      : bookedMonths.length
        ? chart('line', {
          compact: true,
          titleKey: 'adv.rpt.chart.cumulativeResult',
          categories: bookedMonths.map((month, index) => ({ key: 'adv.rpt.month.' + (MONTH_KEYS[index] || 'jan') })),
          series: [{
            id: 'result',
            labelKey: 'adv.rpt.chart.cumulativeResult',
            values: bookedMonths.map((month) => {
              cumulative += num(month.inc) - num(month.cost);
              return round(cumulative);
            }),
          }],
        })
        : null;
    return section('hovedtall', 'adv.rpt.sec.keyFigures', [
      keyFigures(ctx),
      visual,
      ctx.previousYearAvailable ? null : note('info', 'adv.rpt.note.noPreviousYear', { year: ctx.year - 1 }),
    ]);
  };

  // ---------- Summary ----------
  // The board report can show how booked revenue becomes the period result.
  // Other templates keep the prose summary. Exact figures also live in the
  // lead KPI cards, so the chart is a visual reading of the same source.
  const summarySection = (ctx, { visual = false } = {}) => {
    const { num, round, round1, section, text, note, empty, chart } = B();
    const income = num(ctx.report && ctx.report.inntekter);
    const costs = num(ctx.report && ctx.report.kostnader);
    const result = num(ctx.report && ctx.report.resultat);
    const hasBookkeeping = income !== 0 || costs !== 0;
    if (!hasBookkeeping) {
      return section('sammendrag', 'adv.rpt.sec.summary', [empty('adv.rpt.empty.noBookkeeping', { year: ctx.year })]);
    }
    if (!visual) {
      const margin = income > 0 ? round1((result / income) * 100) : null;
      return section('sammendrag', 'adv.rpt.sec.summary', [
        text(ctx.period?.quarter ? result >= 0 ? 'adv.rpt.period.profit' : 'adv.rpt.period.loss' : result >= 0 ? 'adv.rpt.summary.profit' : 'adv.rpt.summary.loss', {
          quarter: ctx.period?.quarter,
          year: ctx.year,
          months: ctx.monthsElapsed,
          income: round(income),
          result: round(Math.abs(result)),
        }),
        margin == null ? null : text('adv.rpt.summary.margin', { margin }),
        !ctx.period?.quarter && ctx.monthsElapsed < 12 ? note('info', 'adv.rpt.note.partialYear', { months: ctx.monthsElapsed, year: ctx.year }) : null,
      ]);
    }
    return section('sammendrag', 'adv.rpt.sec.summary', [
      chart('waterfall', {
        compact: true,
        titleKey: 'adv.rpt.chart.summaryResult',
        opening: round(income),
        openingLabel: { key: 'adv.rpt.kpi.income' },
        categories: [{ key: 'adv.rpt.kpi.costs' }],
        series: [{ id: 'costs', labelKey: 'adv.rpt.kpi.costs', values: [-round(costs)] }],
        closing: round(result),
        closingLabel: { key: 'adv.rpt.kpi.result' },
      }),
      !ctx.period?.quarter && ctx.monthsElapsed < 12 ? note('info', 'adv.rpt.note.partialYear', { months: ctx.monthsElapsed, year: ctx.year }) : null,
    ]);
  };

  // ---------- Budget vs actual ----------
  // The plan model compares booked figures against budget-to-date, not against
  // the full-year budget — that distinction is the whole reason it exists, so
  // this section never re-derives it.
  const budgetSection = (ctx) => {
    const { num, safeArray, section, text, note, empty, money, chart } = B();
    const plan = ctx.plan;
    if (!plan || !plan.hasBudget) {
      return section('budsjett', 'adv.rpt.sec.budget', [empty('adv.rpt.empty.noBudget', { year: ctx.year })]);
    }
    const totals = plan.totals || {};
    const incomeVariance = num(totals.incomeActual) - num(totals.incomeBudgetToDate);
    const costVariance = num(totals.costActual) - num(totals.costBudgetToDate);
    const notable = safeArray(plan.notable);
    const unbudgeted = safeArray(plan.unbudgeted);
    return section('budsjett', 'adv.rpt.sec.budget', [
      text(ctx.period?.quarter ? 'adv.rpt.period.budget' : 'adv.rpt.budget.intro', { months: plan.monthsElapsed, year: plan.year }),
      chart('bars', {
        titleKey: 'adv.rpt.chart.budgetActual',
        categories: [{ key: 'adv.rpt.row.income' }, { key: 'adv.rpt.row.costs' }],
        series: [
          { id: 'budget', labelKey: 'adv.rpt.col.budgetToDate', values: [num(totals.incomeBudgetToDate), num(totals.costBudgetToDate)] },
          { id: 'actual', labelKey: 'adv.rpt.col.actual', values: [num(totals.incomeActual), num(totals.costActual)] },
        ],
      }),
      {
        type: 'table',
        cols: [{ key: 'adv.rpt.col.line' }, { key: 'adv.rpt.col.budgetToDate' }, { key: 'adv.rpt.col.actual' }, { key: 'adv.rpt.col.variance' }],
        rows: [
          [{ key: 'adv.rpt.row.income' }, money(totals.incomeBudgetToDate), money(totals.incomeActual), { ...money(incomeVariance), tone: incomeVariance < 0 ? 'danger' : 'success' }],
          [{ key: 'adv.rpt.row.costs' }, money(totals.costBudgetToDate), money(totals.costActual), { ...money(costVariance), tone: costVariance > 0 ? 'danger' : 'success' }],
        ],
      },
      notable.length
        ? {
          type: 'table',
          cols: [{ key: 'adv.rpt.col.account' }, { key: 'adv.rpt.col.budgetToDate' }, { key: 'adv.rpt.col.actual' }, { key: 'adv.rpt.col.variance' }],
          rows: notable.map((row) => [
            { text: row.name },
            money(row.budgetToDate),
            money(row.actual),
            { ...money(row.variance), tone: row.unfavourable ? 'danger' : 'success' },
          ]),
        }
        : note('info', 'adv.rpt.note.noMaterialVariance'),
      unbudgeted.length ? note('warning', 'adv.rpt.note.unbudgeted', { count: unbudgeted.length }) : null,
    ]);
  };

  // ---------- Liquidity ----------
  // The forecast curve is a running balance of BOOKED figures, so months ahead
  // of today stand flat by design. The note says that in words rather than
  // letting a flat line read as a prediction.
  const liquiditySection = (ctx) => {
    const { num, safeArray, round, section, note, empty, chart, MONTH_KEYS } = B();
    const advisor = ctx.advisor;
    const forecast = safeArray(advisor && advisor.forecast);
    const overview = (ctx.home && ctx.home.overview) || {};
    const receivables = num(overview.salesCurrent) + num(overview.salesOverdue);
    const payables = num(overview.purchasesCurrent) + num(overview.purchasesOverdue);
    if (!forecast.length && !receivables && !payables) {
      return section('likviditet', 'adv.rpt.sec.liquidity', [empty('adv.rpt.empty.noLiquidityBasis')]);
    }
    const buffer = num(advisor && advisor.buffer);
    const lowest = forecast.length ? Math.min(...forecast.map((month) => num(month.balance))) : null;
    return section('likviditet', 'adv.rpt.sec.liquidity', [
      {
        type: 'metrics',
        items: [
          { id: 'receivables', label: { key: 'adv.rpt.metric.receivables' }, value: round(receivables), format: 'currency' },
          { id: 'payables', label: { key: 'adv.rpt.metric.payables' }, value: round(payables), format: 'currency' },
          { id: 'buffer', label: { key: 'adv.rpt.metric.buffer' }, value: round(buffer), format: 'currency' },
          {
            id: 'lowest',
            label: { key: 'adv.rpt.metric.lowestBalance' },
            value: lowest == null ? null : round(lowest),
            format: 'currency',
            tone: lowest != null && lowest < buffer ? 'danger' : 'success',
          },
        ],
      },
      forecast.length ? chart('line', {
        compact: true,
        titleKey: 'adv.rpt.chart.runningBalance',
        categories: forecast.map((month, index) => ({ key: 'adv.rpt.month.' + (MONTH_KEYS[index] || 'jan') })),
        series: [{ id: 'balance', labelKey: 'adv.rpt.col.balance', values: forecast.map((month) => num(month.balance)) }],
        marker: { labelKey: 'adv.rpt.metric.buffer', value: buffer },
      }) : null,
      (receivables || payables) ? chart('bars', {
        compact: true,
        titleKey: 'adv.rpt.chart.openItemsDue',
        categories: [{ key: 'adv.rpt.metric.receivables' }, { key: 'adv.rpt.metric.payables' }],
        series: [
          { id: 'current', labelKey: 'adv.rpt.row.current', values: [num(overview.salesCurrent), num(overview.purchasesCurrent)] },
          { id: 'overdue', labelKey: 'adv.rpt.row.overdue', values: [num(overview.salesOverdue), num(overview.purchasesOverdue)] },
        ],
      }) : null,
      lowest == null ? empty('adv.rpt.empty.noForecast') : lowest < buffer
        ? note('warning', 'adv.rpt.note.belowBuffer', { buffer: round(buffer), lowest: round(lowest) })
        : note('success', 'adv.rpt.note.aboveBuffer', { buffer: round(buffer) }),
      note('info', 'adv.rpt.note.forecastIsBooked'),
    ]);
  };

  // ---------- Running balance ----------
  const forecastSection = (ctx) => {
    const { num, safeArray, section, note, empty, money, chart, MONTH_KEYS } = B();
    const forecast = safeArray(ctx.advisor && ctx.advisor.forecast);
    if (!forecast.length) return section('prognose', 'adv.rpt.sec.forecast', [empty('adv.rpt.empty.noForecast')]);
    return section('prognose', 'adv.rpt.sec.forecast', [
      chart('line', {
        categories: forecast.map((month, index) => ({ key: 'adv.rpt.month.' + (MONTH_KEYS[index] || 'jan') })),
        series: [{ id: 'balance', labelKey: 'adv.rpt.col.balance', values: forecast.map((month) => num(month.balance)) }],
        // The buffer is the line the balance must not cross, so the chart draws
        // it rather than leaving the reader to compare against a note.
        marker: { labelKey: 'adv.rpt.metric.buffer', value: num(ctx.advisor && ctx.advisor.buffer) },
      }),
      {
        type: 'table',
        cols: [{ key: 'adv.rpt.col.month' }, { key: 'adv.rpt.col.in' }, { key: 'adv.rpt.col.out' }, { key: 'adv.rpt.col.balance' }, { key: 'adv.rpt.col.certainty' }],
        rows: forecast.map((month, index) => [
          { key: 'adv.rpt.month.' + (MONTH_KEYS[index] || 'jan') },
          money(month.inn),
          money(month.ut),
          money(month.balance),
          { key: 'adv.rpt.certainty.' + (month.certainty || 'uncertain') },
        ]),
      },
      note('info', 'adv.rpt.note.forecastIsBooked'),
    ]);
  };

  // ---------- Balance sheet and solidity ----------
  // Assets and equity/liabilities come from the trial balance groups the
  // reports page already prints. Solidity comes from the reconciled equity
  // read model, which includes the period result without double-counting a
  // result that has already been transferred to equity accounts.
  const balanceSection = (ctx) => {
    const { num, safeArray, round, round1, section, note, empty, chart } = B();
    const groups = safeArray(ctx.trial);
    const sumGroup = (key) => {
      const group = groups.find((entry) => entry.key === key);
      return safeArray(group && group.accounts).reduce((total, account) => total + num(account.ut), 0);
    };
    const assets = sumGroup('eiendeler');
    const equityAndDebt = sumGroup('egenkapital');
    if (!assets && !equityAndDebt) {
      return section('balanse', 'adv.rpt.sec.balance', [empty('adv.rpt.empty.noBalance', { year: ctx.year })]);
    }
    const solidity = ctx.equityReconciliation && ctx.equityReconciliation.main
      ? ctx.equityReconciliation.main.equityRatio
      : null;
    const validSolidity = solidity != null && Number.isFinite(Number(solidity))
      && Number(solidity) >= 0 && Number(solidity) <= 100;
    return section('balanse', 'adv.rpt.sec.balance', [
      {
        type: 'metrics',
        items: [
          { id: 'assets', label: { key: 'adv.rpt.metric.assets' }, value: round(assets), format: 'currency' },
          // The trial balance carries equity and liabilities as one group.
          // Splitting them here would mean deciding which accounts are equity —
          // a chart-of-accounts question this file must not answer on its own.
          { id: 'equityDebt', label: { key: 'adv.rpt.metric.equityAndDebt' }, value: round(Math.abs(equityAndDebt)), format: 'currency' },
          { id: 'solidity', label: { key: 'adv.rpt.metric.solidity' }, value: solidity == null ? null : round1(solidity), format: 'percent' },
        ],
      },
      chart('bars', {
        compact: true,
        titleKey: 'adv.rpt.chart.balanceBasis',
        categories: [{ key: 'adv.rpt.metric.assets' }, { key: 'adv.rpt.metric.equityAndDebt' }],
        series: [{ id: 'balance', labelKey: 'adv.rpt.chart.bookedBalance', values: [round(assets), round(Math.abs(equityAndDebt))] }],
      }),
      validSolidity ? chart('bars', {
        compact: true,
        titleKey: 'adv.rpt.chart.capitalStructure',
        categories: [{ key: 'adv.rpt.chart.equityShare' }, { key: 'adv.rpt.chart.otherFinancing' }],
        series: [{ id: 'share', labelKey: 'adv.rpt.col.share', values: [round1(solidity), round1(100 - solidity)] }],
      }) : null,
      note('info', 'adv.rpt.note.balanceGrouping'),
      note('info', 'adv.rpt.note.balanceInterimBasis'),
    ]);
  };

  // ---------- Result development per month ----------
  const resultTrendSection = (ctx) => {
    const { num, safeArray, section, empty, money, chart, MONTH_KEYS } = B();
    const months = safeArray(ctx.chart);
    if (!months.some((month) => num(month.inc) || num(month.cost))) {
      return section('resultat', 'adv.rpt.sec.resultTrend', [empty('adv.rpt.empty.noBookkeeping', { year: ctx.year })]);
    }
    const monthTone = (month) => {
      const income = num(month.inc);
      const costs = num(month.cost);
      if (!income && !costs) return 'idle';
      const result = income - costs;
      return result > 0 ? 'success' : result < 0 ? 'danger' : null;
    };
    const monthLabels = months.map((month, index) => ({
      key: 'adv.rpt.month.' + (MONTH_KEYS[index + (ctx.chartMonthStart || 0)] || 'jan'), tone: monthTone(month),
    }));
    return section('resultat', 'adv.rpt.sec.resultTrend', [
      chart('bars', {
        titleKey: 'adv.rpt.chart.monthlyResult',
        colorBySign: true,
        categories: monthLabels,
        series: [{ id: 'result', labelKey: 'adv.rpt.col.result',
          values: months.map((month) => num(month.inc) - num(month.cost)) }],
      }),
      chart('bars', {
        titleKey: 'adv.rpt.chart.incomeCosts',
        categories: monthLabels,
        series: [
          { id: 'inc', labelKey: 'adv.rpt.col.income', values: months.map((month) => num(month.inc)) },
          { id: 'cost', labelKey: 'adv.rpt.col.costs', values: months.map((month) => num(month.cost)) },
        ],
      }),
      {
        type: 'table',
        cols: [{ key: 'adv.rpt.col.month' }, { key: 'adv.rpt.col.income' }, { key: 'adv.rpt.col.costs' }, { key: 'adv.rpt.col.result' }],
        rows: months.map((month, index) => {
          const monthResult = num(month.inc) - num(month.cost);
          return [
            monthLabels[index],
            money(month.inc),
            money(month.cost),
            { ...money(monthResult), tone: monthTone(month) },
          ];
        }),
      },
    ]);
  };

  return { summarySection, keyFiguresSection, budgetSection, liquiditySection, forecastSection, balanceSection, resultTrendSection };
})();

Object.assign(window, { PlummeAiAdvisorReportFinance });
